import { findFirstNonEmptyString } from "@product/scmp-sdk";
import type { Dispatch, SetStateAction } from "react";
import { forwardRef, useEffect, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { useArticleRenderContext } from "~/components/article/article-render/contexts";
import { useEnableGenericInlineWidgetByUserSubscription } from "~/components/article/article-speech/article-speech-inline-widget/variants/generic/hooks";
import { useArticleSpeechPlayerController } from "~/components/article/article-speech/article-speech-player/hooks";
import { useArticleSpeechContext } from "~/components/article/article-speech/contexts";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import { ZIndexLayer } from "~/lib/styles";
import type { buttonArticleSpeechInlineWidgetGenericButtonArticle$key } from "~/queries/__generated__/buttonArticleSpeechInlineWidgetGenericButtonArticle.graphql";

import { NumberOfCharacterShow } from "./consts";
import type { HeadphoneIconTheme } from "./headphone-icon";
import { HeadphoneIcon } from "./headphone-icon";
import {
  MainContainer,
  MinifyContainer,
  PausedContainer,
  RegularContainer,
  StyledArrowRightIcon,
  StyledCloseIcon,
  Text,
} from "./styles";

type Props = {
  extendClick?: () => void;
  headphoneIconTheme?: HeadphoneIconTheme;
  isMinify: boolean;
  reference: buttonArticleSpeechInlineWidgetGenericButtonArticle$key;
  setIsTooltipActive: Dispatch<SetStateAction<boolean>>;
  withMinifyText?: boolean;
  zIndex?: string;
};

export const ArticleSpeechInlineWidgetGenericButton = forwardRef<HTMLDivElement, Props>(
  (
    {
      headphoneIconTheme,
      isMinify = false,
      withMinifyText = true,
      zIndex = `${ZIndexLayer.Drawer - 1}`,
      ...props
    },
    reference,
  ) => {
    const article = useFragment(
      graphql`
        fragment buttonArticleSpeechInlineWidgetGenericButtonArticle on Article {
          entityId
          socialHeadline
          headline
        }
      `,
      props.reference,
    );

    const { articleSpeechifyPlayer, articleSpeechifyPlayerState } = useArticleSpeechContext();
    const {
      handleCheckIsCurrentArticleLinkedToWidget,
      handleCurrentPlayerUnmountAndCleanUp,
      handleInitNewPlayerAndPlay,
    } = useArticleSpeechPlayerController();
    const { articleElementReference } = useArticleRenderContext();
    const { isWidgetEnabledByUser } = useEnableGenericInlineWidgetByUserSubscription();
    const { percentageReached, setPercentageReached } = useArticleRenderContext();

    const isWidgetUnmounted = useMemo(
      () => articleSpeechifyPlayerState === "unmounted",
      [articleSpeechifyPlayerState],
    );

    const isWidgetLoading = useMemo(
      () =>
        articleSpeechifyPlayerState === "loading" &&
        handleCheckIsCurrentArticleLinkedToWidget(articleElementReference?.current),
      [
        articleElementReference,
        articleSpeechifyPlayerState,
        handleCheckIsCurrentArticleLinkedToWidget,
      ],
    );

    const isWidgetPlaying = useMemo(
      () =>
        articleSpeechifyPlayerState === "playing" &&
        handleCheckIsCurrentArticleLinkedToWidget(articleElementReference?.current),
      [
        articleElementReference,
        articleSpeechifyPlayerState,
        handleCheckIsCurrentArticleLinkedToWidget,
      ],
    );

    const isWidgetPaused = useMemo(
      () =>
        articleSpeechifyPlayerState === "paused" &&
        handleCheckIsCurrentArticleLinkedToWidget(articleElementReference?.current),
      [
        articleElementReference,
        articleSpeechifyPlayerState,
        handleCheckIsCurrentArticleLinkedToWidget,
      ],
    );

    const handleAnalyticsEventsAndTriggerToolTip = () => {
      sendGATracking({
        action: "Subscription Entry Point/TTS/Impression",
        category: "Subscription Entry Point",
        label: window.location.href,
      });

      sendGA4Tracking({
        action: "imp",
        category: "subs",
        customized_parameters: {
          article_id: article.entityId,
          cta_type: "subscribe",
          trigger_point: "text to speech",
        },
        subcategory: "entry_point",
      });

      props.setIsTooltipActive(true);
    };

    const handleTriggerArticleSpeechPlayer = async () => {
      if (
        !isWidgetUnmounted &&
        handleCheckIsCurrentArticleLinkedToWidget(articleElementReference?.current)
      )
        return;

      if (articleSpeechifyPlayer.current) await handleCurrentPlayerUnmountAndCleanUp();

      props.extendClick?.();
      await handleInitNewPlayerAndPlay({
        extendedEvents: {
          play: [
            () => {
              setPercentageReached?.(undefined);
            },
          ],
        },
        gaEventLabel: isMinify ? "sidebar_button" : "article_button",
        playerConfig: {
          bottomCTASettings: {
            customText: `${findFirstNonEmptyString(article.socialHeadline, article.headline).slice(
              0,
              NumberOfCharacterShow,
            )}...`,
            linkEnabled: false,
          },
          rootElement: articleElementReference?.current ?? undefined,
        },
        zIndex: String(zIndex),
      });
    };

    const handlePlayClick = () => {
      if (!isWidgetEnabledByUser) {
        handleAnalyticsEventsAndTriggerToolTip();
        return;
      }

      void handleTriggerArticleSpeechPlayer();
    };

    useEffect(() => {
      if (
        !handleCheckIsCurrentArticleLinkedToWidget(articleElementReference?.current) ||
        !percentageReached
      )
        return;
      articleSpeechifyPlayer.current?.minimizeWidget();
    }, [
      articleElementReference,
      articleSpeechifyPlayer,
      handleCheckIsCurrentArticleLinkedToWidget,
      percentageReached,
    ]);

    const handleRenderHeadphoneIcon = () => (
      <HeadphoneIcon
        isLoading={isWidgetLoading}
        isPaused={isWidgetPaused}
        isPlayed={isWidgetPlaying}
        theme={headphoneIconTheme}
      />
    );

    const handleRenderMinifyUI = () => {
      if (isWidgetLoading || isWidgetPlaying || isWidgetPaused) {
        return (
          <MinifyContainer $withMinifyText={withMinifyText}>
            {handleRenderHeadphoneIcon()}
          </MinifyContainer>
        );
      }

      return (
        <MinifyContainer $withMinifyText={withMinifyText} onClick={() => handlePlayClick()}>
          {withMinifyText && <Text>Listen</Text>}
          {handleRenderHeadphoneIcon()}
        </MinifyContainer>
      );
    };

    const handleRenderRegularUI = () => {
      if (isWidgetLoading) {
        return <PausedContainer>{handleRenderHeadphoneIcon()}</PausedContainer>;
      }

      if (isWidgetPlaying) {
        return (
          <RegularContainer>
            {handleRenderHeadphoneIcon()}
            <Text>Listening...</Text>
            <StyledCloseIcon
              onClick={() => {
                void handleCurrentPlayerUnmountAndCleanUp?.();
              }}
            />
          </RegularContainer>
        );
      }

      if (isWidgetPaused) {
        return (
          <RegularContainer>
            {handleRenderHeadphoneIcon()}
            <Text>Paused...</Text>
            <StyledCloseIcon
              onClick={() => {
                void handleCurrentPlayerUnmountAndCleanUp?.();
              }}
            />
          </RegularContainer>
        );
      }

      return (
        <RegularContainer onClick={() => handlePlayClick()}>
          {handleRenderHeadphoneIcon()}
          <Text>Listen to this article</Text>
          <StyledArrowRightIcon />
        </RegularContainer>
      );
    };

    return (
      <MainContainer {...props} $isGreyedOut={isWidgetPlaying || isWidgetPaused} ref={reference}>
        {isMinify ? handleRenderMinifyUI() : handleRenderRegularUI()}
      </MainContainer>
    );
  },
);

ArticleSpeechInlineWidgetGenericButton.displayName = "ArticleSpeechInlineWidgetGenericButton";
